import { useEffect, useMemo, useRef } from "react";
import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import type { PluggableList } from "unified";
import { CITATION_CLASSNAME, rehypeLinkifySources } from "../lib/rehype-plugins";
import type { RehypeReference } from "../lib/rehype-plugins";
import { stripCitationMarkers } from "../lib/utils";

// Renders a lone "\n" as a hard line break instead of CommonMark's default
// soft break (collapsed to a space) — required since collapse_paragraph_breaks
// (output.single-linebreak, #379) uses single newlines as the line separator.
const REMARK_PLUGINS: PluggableList = [remarkBreaks];

export interface ProposedAnswerReference {
  id: string;
  index: number;
  quoteStart?: string | null;
  type?: string;
}

interface ProposedAnswerParagraphProps {
  paragraph: string;
  messageId: string;
  references: ProposedAnswerReference[];
  /** Currently hovered reference ID — drives inline text highlight */
  hoveredReferenceId: string | null;
  onCitationClick?: (messageId: string, referenceId: string) => void;
  onCitationHover?: (referenceId: string | null) => void;
}

/**
 * Renders a single proposed-answer paragraph with inline citation links and
 * quote-start–based text highlighting when a reference is hovered.
 *
 * Shared between Venus (AgentProposedAnswer) and Neptune (ProposedAnswerTab).
 */

// Marks that were created by splitting a text node register an exact undo
// here. Cleanup must restore the DOM byte-for-byte: leftover split text
// nodes make the next hover walk a different DOM and highlight a different
// range, and blanket parent.normalize() would merge unrelated text nodes
// that React still holds references to.
const highlightUndo = new WeakMap<HTMLElement, () => void>();
export function ProposedAnswerParagraph({
  paragraph,
  messageId,
  references,
  hoveredReferenceId,
  onCitationClick,
  onCitationHover,
}: ProposedAnswerParagraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const quoteStartByRefId = useMemo(() => {
    const map = new Map<string, string>();
    for (const ref of references) {
      if (ref.quoteStart) map.set(ref.id, ref.quoteStart);
    }
    return map;
  }, [references]);

  const rehypeRefs: RehypeReference[] = useMemo(
    () => references.map((r) => ({ id: r.id, index: r.index, type: r.type })),
    [references],
  );

  const rehypePlugins = useMemo(
    () =>
      rehypeRefs.length > 0
        ? [
            [
              rehypeLinkifySources,
              messageId,
              onCitationClick ?? (() => {}),
              {
                hoverCallback: onCitationHover ?? undefined,
                className: CITATION_CLASSNAME,
              },
              rehypeRefs,
            ],
          ] as import("unified").PluggableList
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageId, rehypeRefs],
  );

  // Highlight the quoted text when a reference is hovered
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function removeHighlights() {
      container?.querySelectorAll("mark.citation-highlight").forEach((m) => {
        const mark = m as HTMLElement;
        const undo = highlightUndo.get(mark);
        if (undo) {
          highlightUndo.delete(mark);
          undo();
          return;
        }
        // Wrap-style marks hold the original (whole) nodes — move them back.
        const parent = mark.parentNode;
        if (parent) {
          while (mark.firstChild) {
            parent.insertBefore(mark.firstChild, mark);
          }
          parent.removeChild(mark);
        }
      });
    }

    // Remove previous highlights
    removeHighlights();

    if (!hoveredReferenceId) return;

    const quoteStart = quoteStartByRefId.get(hoveredReferenceId);
    if (quoteStart) {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      let node: Text | null;
      let marked = false;
      while ((node = walker.nextNode() as Text | null)) {
        const original = node.nodeValue ?? "";
        const idx = original.toLowerCase().indexOf(quoteStart.toLowerCase());
        if (idx >= 0) {
          const parent = node.parentNode;
          if (parent) {
            // Split the node around the match by hand (not surroundContents)
            // so the undo can restore the original single text node exactly.
            const targetNode = node;
            const after = original.slice(idx + quoteStart.length);
            const mark = document.createElement("mark");
            mark.className = "citation-highlight bg-primary/30 rounded-sm";
            mark.textContent = original.slice(idx, idx + quoteStart.length);
            targetNode.nodeValue = original.slice(0, idx);
            parent.insertBefore(mark, targetNode.nextSibling);
            const afterNode = after ? document.createTextNode(after) : null;
            if (afterNode) parent.insertBefore(afterNode, mark.nextSibling);
            highlightUndo.set(mark, () => {
              targetNode.nodeValue = original;
              afterNode?.remove();
              mark.remove();
            });
            marked = true;
          }
          break;
        }
      }
      if (marked) return;
      // quoteStart usually quotes the cited SOURCE (email, memory doc), so it
      // often does not occur in the answer text at all. Fall through to the
      // sentence fallback instead of leaving the hover without any highlight.
    }

    // Fallback: highlight the sentence preceding the hovered citation link
    const citationLinks = container.querySelectorAll(
      `a[data-citation-ref="${hoveredReferenceId}"]`,
    );
    if (citationLinks.length === 0) return;

    for (const link of citationLinks) {
      const parent = link.parentElement;
      if (!parent) continue;

      const children = Array.from(parent.childNodes);
      const linkIndex = children.indexOf(link);
      let textToHighlight = "";
      const nodesToWrap: ChildNode[] = [];

      for (let i = linkIndex - 1; i >= 0; i--) {
        const node = children[i];
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent ?? "";
          const sentenceEnd = Math.max(
            text.lastIndexOf(". "),
            text.lastIndexOf("! "),
            text.lastIndexOf("? "),
            text.lastIndexOf("\n"),
          );
          if (sentenceEnd >= 0) {
            const splitNode = node;
            const applySplitMark = (markText: string, remainder: string) => {
              const mark = document.createElement("mark");
              mark.className = "citation-highlight bg-primary/30 rounded-sm";
              mark.textContent = markText;
              splitNode.textContent = remainder;
              parent.insertBefore(mark, splitNode.nextSibling);
              highlightUndo.set(mark, () => {
                splitNode.textContent = text;
                mark.remove();
              });
            };
            const highlightText = text.slice(sentenceEnd + 2);
            if (highlightText.trim()) {
              applySplitMark(highlightText, text.slice(0, sentenceEnd + 2));
            } else {
              const fullText = text.trimEnd();
              if (fullText.trim()) {
                applySplitMark(fullText, "");
              }
            }
            break;
          }
          textToHighlight = text + textToHighlight;
          nodesToWrap.unshift(node);
        } else if ((node as Element).hasAttribute?.("data-citation-ref")) {
          // A sibling citation chip ([1][2] groups cite the same sentence):
          // skip over it and keep walking back to the supported text.
          continue;
        } else {
          break;
        }
      }

      if (nodesToWrap.length > 0 && textToHighlight.trim()) {
        const mark = document.createElement("mark");
        mark.className = "citation-highlight bg-primary/30 rounded-sm";
        parent.insertBefore(mark, nodesToWrap[0]);
        for (const n of nodesToWrap) mark.appendChild(n);
      }
    }

    return () => {
      removeHighlights();
    };
    // `paragraph` is a dependency because the markdown now reconciles in
    // place when the text changes (no content-derived key): a highlight
    // <mark> anchored to the old text must be removed and re-applied
    // against the freshly rendered DOM.
  }, [hoveredReferenceId, quoteStartByRefId, paragraph]);

  return (
    <div ref={containerRef}>
      {rehypePlugins ? (
        // No content-derived key: React updates the rendered markdown in place
        // when the text changes by a marker rewrite or trailing-whitespace
        // normalization. Keying by the paragraph text would remount the whole
        // subtree on every such change, flashing identical content.
        <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={rehypePlugins}>
          {paragraph}
        </Markdown>
      ) : (
        // Without references, citation markers ("[ref:1, ref:2]") cannot
        // render as chips, so hide them in the displayed text. Display-only:
        // copy/insert paths strip markers from the raw answer themselves.
        // Rendered through Markdown — the same element shape as the chips
        // branch above — so when references arrive the paragraph reconciles
        // in place and only the chips appear, instead of remounting (and
        // re-breaking lines) because a <p> turned into markdown output.
        <Markdown remarkPlugins={REMARK_PLUGINS}>{stripCitationMarkers(paragraph)}</Markdown>
      )}
    </div>
  );
}
